import React, { useState, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import '../ProjectModal.css';

// Fix for default marker icon issue in Leaflet + React
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';

let DefaultIcon = L.icon({
    iconUrl: icon,
    shadowUrl: iconShadow,
    iconSize: [25, 41],
    iconAnchor: [12, 41]
});
L.Marker.prototype.options.icon = DefaultIcon;

function LocationMarker({ position, setPosition, disabled }) {
    useMapEvents({
        click(e) {
            if (disabled) return;
            setPosition(e.latlng);
        },
    });

    return position === null ? null : (
        <Marker position={position}></Marker>
    );
}

const MapResizer = ({ center, zoom }) => {
    const map = useMapEvents({});
    useEffect(() => {
        map.setView(center, zoom || map.getZoom());
    }, [center, zoom, map]);
    return null;
};

/**
 * @component ProjectLocationMap
 * @description Modular map component with search and marker placement.
 */
const ProjectLocationMap = ({ 
    latitude, 
    longitude, 
    onLocationSelect, 
    disabled,
    searchQuery: initialSearchQuery
}) => {
    const [searchQuery, setSearchQuery] = useState(initialSearchQuery || '');
    const [suggestions, setSuggestions] = useState([]);
    const [isSearching, setIsSearching] = useState(false);
    const [mapZoom, setMapZoom] = useState(latitude ? 15 : 6);

    useEffect(() => {
        setSearchQuery(initialSearchQuery || '');
    }, [initialSearchQuery]);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (!e.target.closest('.map-search-container')) {
                setSuggestions([]);
            }
        };
        document.addEventListener('click', handleClickOutside);
        return () => document.removeEventListener('click', handleClickOutside);
    }, []);

    useEffect(() => {
        const fetchSuggestions = async () => {
            if (searchQuery.length < 3) {
                setSuggestions([]);
                return;
            }
            try {
                const token = process.env.REACT_APP_LOCATIONIQ_TOKEN;
                const res = await fetch(`https://eu1.locationiq.com/v1/autocomplete.php?key=${token}&q=${encodeURIComponent(searchQuery)}&format=json&limit=5`);
                const data = await res.json();
                if (Array.isArray(data)) {
                    setSuggestions(data);
                } else {
                    setSuggestions([]);
                }
            } catch (err) {
                console.error('Autocomplete Error:', err);
                setSuggestions([]);
            }
        };

        const timer = setTimeout(() => {
            fetchSuggestions();
        }, 300);

        return () => clearTimeout(timer);
    }, [searchQuery]);

    const handleSearch = async (e) => {
        if (e) e.preventDefault();
        if (!searchQuery) return;
        setIsSearching(true);
        try {
            const token = process.env.REACT_APP_LOCATIONIQ_TOKEN;
            const res = await fetch(`https://eu1.locationiq.com/v1/search.php?key=${token}&q=${encodeURIComponent(searchQuery)}&format=json&limit=1`);
            const data = await res.json();
            if (data && data.length > 0) {
                const { lat, lon, display_name } = data[0];
                const newLat = parseFloat(lat);
                const newLon = parseFloat(lon);
                onLocationSelect({ lat: newLat, lng: newLon }, display_name);
                setMapZoom(15);
                setSuggestions([]);
            } else {
                alert('Ort nicht gefunden.');
            }
        } catch (err) {
            console.error('Search Error:', err);
            alert('Fehler bei der Suche.');
        } finally {
            setIsSearching(false);
        }
    };

    const mapCenter = latitude && longitude ? [latitude, longitude] : [51.1657, 10.4515];

    return (
        <div className="form-group" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <label>Standort auf Karte festlegen</label>

            {!disabled && (
                <div className="map-search-bar" style={{ display: 'flex', gap: '5px', marginBottom: '10px' }}>
                    <div className="map-search-container" style={{ position: 'relative', flex: 1 }}>
                        <input
                            type="text"
                            className="search-input"
                            placeholder="Ort suchen (z.B. Berlin, Musterstraße 1)..."
                            value={searchQuery}
                            onChange={e => setSearchQuery(e.target.value)}
                            onKeyDown={e => e.key === 'Enter' && handleSearch(e)}
                            style={{ margin: 0, width: '100%', height: '36px' }}
                        />
                        {suggestions.length > 0 && (
                            <div style={{
                                position: 'absolute', top: '100%', left: 0, right: 0,
                                backgroundColor: '#ffffff', border: '1px solid #d1d5db',
                                borderRadius: '4px', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                                zIndex: 1010, maxHeight: '200px', overflowY: 'auto'
                            }}>
                                {suggestions.map((s, idx) => (
                                    <div
                                        key={idx}
                                        onClick={() => {
                                            const lat = parseFloat(s.lat);
                                            const lon = parseFloat(s.lon);
                                            onLocationSelect({ lat: lat, lng: lon }, s.display_name);
                                            setSuggestions([]);
                                            setMapZoom(15);
                                        }}
                                        style={{ padding: '8px 12px', cursor: 'pointer', borderBottom: idx < suggestions.length - 1 ? '1px solid #f3f4f6' : 'none', fontSize: '0.85rem', color: '#374151' }}
                                        onMouseEnter={e => e.target.style.backgroundColor = '#f3f4f6'}
                                        onMouseLeave={e => e.target.style.backgroundColor = '#ffffff'}
                                    >
                                        {s.display_name}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                    <button
                        type="button"
                        onClick={handleSearch}
                        disabled={isSearching}
                        style={{
                            padding: 0, width: '40px', flexShrink: 0, alignSelf: 'stretch',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            backgroundColor: '#4f46e5', color: '#ffffff', border: 'none',
                            borderRadius: '4px', cursor: 'pointer'
                        }}
                    >
                        {isSearching ? <span style={{ fontSize: '0.8rem' }}>...</span> : (
                            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ width: '20px', height: '20px' }}>
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                            </svg>
                        )}
                    </button>
                </div>
            )}

            <div style={{ flex: 1, minHeight: '350px', borderRadius: '8px', overflow: 'hidden', border: '1px solid #ddd' }}>
                <MapContainer center={mapCenter} zoom={latitude ? 15 : 6} style={{ height: '100%', width: '100%' }}>
                    <TileLayer
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                    />
                    <LocationMarker
                        position={latitude ? { lat: latitude, lng: longitude } : null}
                        setPosition={(latlng) => onLocationSelect(latlng)}
                        disabled={disabled}
                    />
                    <MapResizer center={mapCenter} zoom={mapZoom} />
                </MapContainer>
            </div>
            
            <div style={{ marginTop: '8px', fontSize: '0.8rem', color: '#666', display: 'flex', justifyContent: 'space-between' }}>
                <div>
                    <span>Lat: {latitude ? latitude.toFixed(6) : '—'}</span>
                    <span style={{ marginLeft: '15px' }}>Lng: {longitude ? longitude.toFixed(6) : '—'}</span>
                </div>
                <span style={{ color: '#4f46e5', fontWeight: '500' }}>Klicken zum Markieren</span>
            </div>
        </div>
    );
};

export default ProjectLocationMap;
